import React, {
  useEffect,
  useRef,
  ReactNode,
  ComponentPropsWithRef,
  forwardRef,
  useImperativeHandle,
  useId,
  useContext,
} from 'react';
import classNames from 'classnames';
import {Checkbox as PathCheckbox} from '@ellucian/react-design-system/core';

import {PathDesignContext} from 'src/components/path-design-context';

import 'stylesheets/components/shared-checkbox.scss';

export interface Props extends ComponentPropsWithRef<'input'> {
  value?: string;
  label?: ReactNode;
  error?: boolean;
  indeterminate?: boolean;
}

const Checkbox = forwardRef<HTMLInputElement, Props>(function Checkbox(
  {label, error, className, indeterminate, ...inputProps},
  forwardedRef
) {
  const inputId = useId();
  const ref = useRef<HTMLInputElement>(null);
  const path = useContext(PathDesignContext);

  // if forwarded a ref, expose existing input ref
  useImperativeHandle(forwardedRef, () => ref.current!, []);

  useEffect(() => {
    if (ref.current && indeterminate !== undefined) {
      ref.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    <div className={classNames('shared-aptitude-checkbox', className)}>
      {path.enabled ? (
        <PathCheckbox {...inputProps} ref={ref} id={inputId} data-testid="shared-checkbox" />
      ) : (
        <input
          ref={ref}
          id={inputId}
          className={classNames('shared-checkbox-input', {error})}
          data-testid="shared-checkbox"
          {...inputProps}
          type="checkbox"
        />
      )}
      <label
        htmlFor={inputId}
        className={classNames('shared-checkbox-input-label', {disabled: inputProps.disabled})}
      >
        {label}
      </label>
    </div>
  );
});

export default Checkbox;
